/**
 * AttachmentList — list, upload, view, and remove files attached to one
 * document (invoice / bill / payment). Used inline on any transaction
 * detail view.
 *
 *   <AttachmentList entityType="invoice" entityId={inv.id} />
 *
 * Behaviour:
 *  - GET on mount; refresh on upload/remove.
 *  - Upload via <input type="file"> — single file at a time.
 *  - "View" opens the file in a new tab using the authenticated blob fetch
 *    (we can't use a plain <a> because the browser drops Authorization).
 *  - "Remove" soft-deletes after confirm.
 */
import { useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { attachmentsApi } from "../lib/business";
import { viewFileInNewTab } from "../lib/api";

interface Props {
  entityType: "invoice" | "bill" | "payment";
  entityId: string;
  /** Disable mutations (read-only display, e.g. on voided documents). */
  readOnly?: boolean;
}

function fmtSize(bytes: string | number): string {
  const n = typeof bytes === "string" ? Number(bytes) : bytes;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function AttachmentList({ entityType, entityId, readOnly = false }: Props) {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const key = ["attachments", entityType, entityId];

  const q = useQuery({
    queryKey: key,
    queryFn: () => attachmentsApi.list(entityType, entityId),
  });

  const uploadM = useMutation({
    mutationFn: (file: File) => attachmentsApi.upload(entityType, entityId, file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key });
      qc.invalidateQueries({ queryKey: ["history", entityType, entityId] });
    },
  });

  const removeM = useMutation({
    mutationFn: (id: string) => attachmentsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key });
      qc.invalidateQueries({ queryKey: ["history", entityType, entityId] });
    },
  });

  const attachments = q.data?.attachments ?? [];

  return (
    <div className="rounded border border-stone-200 bg-white p-3">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-sm font-semibold text-stone-800">
          Attachments {attachments.length > 0 && (
            <span className="text-stone-500 font-normal">({attachments.length})</span>
          )}
        </h4>
        {!readOnly && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadM.isPending}
            className="text-xs font-medium text-emerald-700 hover:text-emerald-800 disabled:opacity-50"
          >
            {uploadM.isPending ? "Uploading…" : "+ Attach file"}
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept=".pdf,.jpg,.jpeg,.png,.webp,.xls,.xlsx,.doc,.docx,.csv,.txt"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) uploadM.mutate(f);
            // Reset so the same file can be picked again after a failure.
            if (fileInputRef.current) fileInputRef.current.value = "";
          }}
        />
      </div>
      {uploadM.isError && (
        <div className="mb-2 rounded bg-rose-50 px-2 py-1 text-xs text-rose-700">
          Upload failed: {(uploadM.error as Error).message}
        </div>
      )}
      {attachments.length === 0 ? (
        <p className="text-xs text-stone-500 italic">No files attached.</p>
      ) : (
        <ul className="divide-y divide-stone-100">
          {attachments.map((a) => (
            <li key={a.id} className="flex items-center justify-between gap-3 py-1.5">
              <div className="min-w-0 flex-1">
                <button
                  type="button"
                  onClick={() => viewFileInNewTab(a.download_url)}
                  className="block truncate text-left text-sm text-emerald-700 hover:underline"
                  title={a.filename}
                >
                  {a.filename}
                </button>
                <div className="text-[11px] text-stone-500">
                  {fmtSize(a.size_bytes)} · {new Date(a.uploaded_at).toLocaleString()}
                </div>
              </div>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => {
                    if (confirm(`Remove ${a.filename}?`)) removeM.mutate(a.id);
                  }}
                  className="text-xs text-stone-400 hover:text-rose-700"
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
